import { useRef } from "react";
import { useTranslations } from "next-intl";

/** Largest accepted image, mirroring the media route's 15MB cap. */
export const MAX_PHOTO_BYTES = 15 * 1024 * 1024;

/**
 * Remove control for photo-pick tiles (join, upload, pending). Visible by
 * default so touch devices always have the affordance; hidden only where a
 * pointer can hover (`pointer-fine`), revealed on tile hover and on keyboard
 * focus — never an invisible hit target.
 */
export const REMOVE_PICK_BUTTON_CLASSES =
  "absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full bg-black/60 text-white transition-opacity pointer-fine:opacity-0 focus-visible:opacity-100 group-hover:opacity-100";

export interface PhotoPick {
  file: File;
  /** Object URL for the local preview (revoked on removal/unmount). */
  url: string;
  /** Stable key shared by upload retries and persisted draft recovery. */
  idempotencyKey: string;
  /** Draft-store id when the pick is persisted to IndexedDB for recovery. */
  draftId?: string;
}

export function PhotoStep({
  picks,
  errorKey,
  onAdd,
  onRemove,
}: {
  picks: PhotoPick[];
  /** i18n key (within `join`) for the "at least one photo" error, or null. */
  errorKey: string | null;
  onAdd: (files: FileList | null) => void;
  onRemove: (index: number) => void;
}) {
  const t = useTranslations("join.photos");
  const tJoin = useTranslations("join");
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">
        {t("title")}
      </h1>
      <p className="mt-2 text-sm text-white/55">{t("subtitle")}</p>

      <div className="mt-6 flex flex-col gap-5">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] px-6 py-10 text-center transition-colors hover:border-white/30"
        >
          <span className="flex size-12 items-center justify-center rounded-2xl bg-flamingo/12 text-flamingo">
            <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 16V4m0 0 4 4m-4-4L8 8" />
              <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
            </svg>
          </span>
          <div>
            <p className="font-medium text-white">{t("dropTitle")}</p>
            <p className="mt-0.5 text-xs text-white/55">{t("dropHint")}</p>
          </div>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            onAdd(e.target.files);
            e.target.value = "";
          }}
        />

        {picks.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {picks.map((p, i) => (
              <div
                key={p.url}
                className="group relative aspect-square overflow-hidden rounded-xl border border-white/8"
              >
                {/* eslint-disable-next-line @next/next/no-img-element -- local object URL preview */}
                <img src={p.url} alt="" className="size-full object-cover" />
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  aria-label={t("remove")}
                  className={REMOVE_PICK_BUTTON_CLASSES}
                >
                  <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {errorKey && (
          <p className="text-xs font-medium text-danger-light">
            {tJoin(errorKey)}
          </p>
        )}
      </div>
    </div>
  );
}
